import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  KeyRound,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  Fingerprint,
  FileText,
  Laptop,
} from "lucide-react";

import {
  api,
  ApiError,
  passkeys,
  staffLoginChallenge,
  staffLoginVerify,
} from "../api/staff";
import {
  getPasskey,
  isPasskeySupported,
  passkeyErrorName,
} from "../api/webauthn";
import { deriveKeyPair } from "../crypto/keys";
import * as mnemonic from "../crypto/mnemonic";
import { hex, sign } from "../crypto/sign";
import { defaultDeviceLabel } from "../utils/device-label";
import { cn } from "../lib/utils";

const STAFF_LOGIN_MESSAGE_PREFIX = "dr:staff-login:v1";

type TabType = "phrase" | "passkey" | "password";

export function Login() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("phrase");

  // State
  const [phrase, setPhrase] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [deviceLabel, setDeviceLabel] = useState(defaultDeviceLabel);
  const [busy, setBusy] = useState<"passkey" | "phrase" | "password" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passkeySupported, setPasskeySupported] = useState(false);

  useEffect(() => {
    void isPasskeySupported().then(setPasskeySupported);
  }, []);

  // Passkey Login
  async function handlePasskey() {
    setBusy("passkey");
    setError(null);
    try {
      const { challengeId, options } = await passkeys.loginOptions();
      const assertion = await getPasskey(options);
      await passkeys.loginVerify(challengeId, assertion);
      await api("/staff/auth/me");
      navigate("/", { replace: true });
    } catch (err) {
      const name = passkeyErrorName(err);
      if (name === "NotAllowedError" || name === "AbortError") {
        setBusy(null);
        return;
      }
      if (err instanceof ApiError) {
        setError(`${err.status} ${err.message}`);
      } else {
        setError("Échec de la connexion par Passkey");
      }
      setBusy(null);
    }
  }

  // Recovery Phrase Login (24 words)
  async function handleRecoveryPhrase(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const normalized = mnemonic.normalize(phrase);
    if (!mnemonic.isValid(normalized)) {
      setError("Veuillez saisir une phrase de récupération staff valide (24 mots).");
      return;
    }
    setBusy("phrase");
    try {
      const seed = mnemonic.toSeed(normalized, "");
      const { secretKey, publicKey } = deriveKeyPair(seed);
      const publicKeyHex = hex.fromBytes(publicKey);

      const challenge = await staffLoginChallenge(publicKeyHex);
      const timestamp = Date.now();
      const message = `${STAFF_LOGIN_MESSAGE_PREFIX}:${challenge.nonce}:${timestamp}`;
      const signature = hex.fromBytes(sign(secretKey, message));

      await staffLoginVerify({
        publicKey: publicKeyHex,
        nonce: challenge.nonce,
        timestamp,
        signature,
        ...(deviceLabel.trim() ? { deviceLabel: deviceLabel.trim() } : {}),
      });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Échec de l'authentification cryptographique staff");
      }
      setBusy(null);
    }
  }

  // Email/Password Login
  async function handleEmailPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy("password");
    try {
      await api("/staff/auth/password-login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Identifiants invalides");
      }
      setBusy(null);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12 select-none">
      <div className="w-full max-w-md space-y-6">
        {/* Brand header */}
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center mb-3 shadow-lg shadow-primary/20 text-primary-foreground">
            <Shield className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Collect & Track
          </h1>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary mt-1">
            Console d'Administration Back-office
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
          {/* Method Tabs */}
          <div className="grid grid-cols-3 gap-1 bg-muted/50 p-1 rounded-xl mb-6 border border-border">
            <button
              type="button"
              onClick={() => { setActiveTab("phrase"); setError(null); }}
              className={cn(
                "flex items-center justify-center gap-1.5 py-2 px-1 text-xs font-semibold rounded-lg transition-colors",
                activeTab === "phrase"
                  ? "bg-card text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <FileText className="w-3.5 h-3.5" />
              Phrase
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab("passkey"); setError(null); }}
              className={cn(
                "flex items-center justify-center gap-1.5 py-2 px-1 text-xs font-semibold rounded-lg transition-colors",
                activeTab === "passkey"
                  ? "bg-card text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Fingerprint className="w-3.5 h-3.5" />
              Passkey
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab("password"); setError(null); }}
              className={cn(
                "flex items-center justify-center gap-1.5 py-2 px-1 text-xs font-semibold rounded-lg transition-colors",
                activeTab === "password"
                  ? "bg-card text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Lock className="w-3.5 h-3.5" />
              Email
            </button>
          </div>

          {/* Error Banner */}
          {error && (
            <div className="mb-5 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs font-medium leading-relaxed">
              {error}
            </div>
          )}

          {/* TAB 1: Phrase Login */}
          {activeTab === "phrase" && (
            <form onSubmit={handleRecoveryPhrase} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Phrase de Récupération (24 mots)
                </label>
                <textarea
                  rows={4}
                  value={phrase}
                  onChange={(e) => setPhrase(e.target.value)}
                  placeholder="Saisissez vos 24 mots séparés par des espaces…"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-input bg-background text-foreground font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                />
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  La phrase est validée et signée localement dans votre navigateur via Ed25519.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Nom de l'appareil
                </label>
                <div className="relative">
                  <Laptop className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={deviceLabel}
                    onChange={(e) => setDeviceLabel(e.target.value)}
                    className="w-full pl-9 pr-3.5 py-2 rounded-xl border border-input bg-background text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={busy !== null || !phrase.trim()}
                className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 px-4 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {busy === "phrase" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signature & Authentification…
                  </>
                ) : (
                  "Se connecter avec la phrase"
                )}
              </button>
            </form>
          )}

          {/* TAB 2: Passkey Login */}
          {activeTab === "passkey" && (
            <div className="space-y-4 py-2 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center text-primary mb-2">
                <Fingerprint className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Authentification Biométrique / FIDO2</h3>
                <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
                  Utilisez Touch ID, Windows Hello ou votre clé physique de sécurité matérielle.
                </p>
              </div>

              <button
                type="button"
                onClick={handlePasskey}
                disabled={busy !== null || !passkeySupported}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm mt-4"
              >
                {busy === "passkey" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Vérification de la clé…
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4" />
                    Déverrouiller avec ma Passkey
                  </>
                )}
              </button>

              {!passkeySupported && (
                <p className="text-[11px] text-amber-500 font-medium">
                  Les passkeys ne sont pas prises en charge par ce navigateur.
                </p>
              )}
            </div>
          )}

          {/* TAB 3: Email & Password */}
          {activeTab === "password" && (
            <form onSubmit={handleEmailPassword} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Email Staff
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="staff@collectandtrack.xyz"
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••••••"
                    className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={busy !== null || !email.trim() || !password}
                className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 px-4 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {busy === "password" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Connexion en cours…
                  </>
                ) : (
                  "Se connecter"
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Écosystème sécurisé · Chiffrement de bout en bout & RBAC
        </p>
      </div>
    </div>
  );
}
